import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Empty, Skeleton, Space, Tag, Typography } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { productApi } from '../../api';

const DAY_LABEL: Record<number, string> = {
  1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun',
};

/**
 * Teacher — My week & proxies. Weekly grid of my slots plus this-week's
 * substitutions on both sides (who I'm covering, who's covering me).
 */
export function TeacherWeekPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [anchor, setAnchor] = useState<string | undefined>(undefined);
  const q = useQuery({
    queryKey: ['teacher-week', institution, anchor],
    queryFn: () => productApi.getTeacherWeek(institution, anchor),
    enabled: !!institution,
  });

  const grid = useMemo(() => {
    const map = new Map<number, Map<number, typeof q.data extends undefined ? never : NonNullable<typeof q.data>['slots'][number]>>();
    let maxPeriod = 0;
    for (const s of q.data?.slots ?? []) {
      if (!map.has(s.day_of_week)) map.set(s.day_of_week, new Map());
      map.get(s.day_of_week)!.set(s.period, s);
      if (s.period > maxPeriod) maxPeriod = s.period;
    }
    return { map, periods: Array.from({ length: maxPeriod }, (_, i) => i + 1) };
  }, [q.data]);

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const w = q.data;

  const shift = (deltaDays: number) => {
    const d = new Date(w.week_start);
    d.setUTCDate(d.getUTCDate() + deltaDays);
    setAnchor(d.toISOString().slice(0, 10));
  };

  const days = [1, 2, 3, 4, 5, 6];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <Typography.Title level={3} style={{ margin: 0 }}>My week</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {w.week_start} → {w.week_end}
          </Typography.Text>
        </div>
        <Button icon={<LeftOutlined />} onClick={() => shift(-7)} />
        <Button onClick={() => setAnchor(undefined)}>This week</Button>
        <Button icon={<RightOutlined />} onClick={() => shift(7)} />
      </div>

      {!w.teacher ? (
        <Alert type="warning" showIcon message="Your login email isn't linked to a staff record." />
      ) : grid.periods.length === 0 ? (
        <Empty description="No periods assigned to you yet." style={{ padding: 20 }} />
      ) : (
        <Card size="small" bodyStyle={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead>
              <tr>
                <th style={cellHead}>Period</th>
                {days.map((d) => (<th key={d} style={cellHead}>{DAY_LABEL[d]}</th>))}
              </tr>
            </thead>
            <tbody>
              {grid.periods.map((p) => (
                <tr key={p}>
                  <td style={{ ...cellBody, fontWeight: 600, background: '#fafbfc' }}>P{p}</td>
                  {days.map((d) => {
                    const s = grid.map.get(d)?.get(p);
                    if (!s) return <td key={d} style={cellBody} />;
                    return (
                      <td key={d} style={{ ...cellBody, background: s.on_leave ? '#fff4f2' : '#eaf3fb' }}>
                        <div style={{ fontSize: 12.5, fontWeight: 600 }}>{s.subject_name}</div>
                        <div style={{ fontSize: 11, color: '#5b6478' }}>
                          {s.grade}{s.section ? `-${s.section}` : ''}
                        </div>
                        {s.on_leave && <Tag color="volcano" style={{ marginTop: 4 }}>on leave</Tag>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card size="small" title={`I'm covering (${w.covering.length})`}>
        {w.covering.length === 0 ? (
          <Empty description="Nothing on your plate this week." style={{ padding: 12 }} />
        ) : w.covering.map((c) => (
          <div key={c.substitution_id} style={rowStyle}>
            <div style={{ flex: 1 }}>
              <b>{c.date}</b> · P{c.period} · {c.subject_name} · {c.grade}{c.section ? `-${c.section}` : ''}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              original: {c.original_teacher_name ?? '—'}
            </Typography.Text>
          </div>
        ))}
      </Card>

      <Card size="small" title={`Covered for me (${w.covered.length})`}>
        {w.covered.length === 0 ? (
          <Empty description="No proxies arranged for you this week." style={{ padding: 12 }} />
        ) : w.covered.map((c) => (
          <div key={c.substitution_id} style={rowStyle}>
            <div style={{ flex: 1 }}>
              <b>{c.date}</b> · P{c.period} · {c.subject_name} · {c.grade}{c.section ? `-${c.section}` : ''}
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              proxy: {c.proxy_teacher_name ?? '—'}
            </Typography.Text>
          </div>
        ))}
      </Card>
    </Space>
  );
}

const cellHead: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', fontSize: 12,
  background: '#f7f7f5', borderBottom: '1px solid #eef0f3', color: '#5b6478',
};
const cellBody: React.CSSProperties = {
  padding: '10px', borderBottom: '1px solid #f1f1f4', verticalAlign: 'top', minWidth: 90,
};
const rowStyle: React.CSSProperties = {
  padding: '8px 0', borderTop: '1px solid #f1f1f4', display: 'flex', gap: 8, alignItems: 'baseline',
  fontSize: 13,
};
